// 性能监控工具

interface PerformanceMetrics {
  startTime: number
  endTime?: number
  duration?: number
  memoryUsage?: any
  renderCount?: number
  operations?: string[]
}

class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetrics> = new Map()
  private renderCounts: Map<string, number> = new Map()

  /**
   * 开始性能监控
   * @param key 监控标识
   */
  start(key: string): void {
    this.metrics.set(key, {
      startTime: performance.now(),
      operations: []
    })
    
    // 记录内存使用情况（如果支持）
    if ('memory' in performance) {
      const metrics = this.metrics.get(key)!
      metrics.memoryUsage = {
        used: (performance as any).memory.usedJSHeapSize,
        total: (performance as any).memory.totalJSHeapSize,
        limit: (performance as any).memory.jsHeapSizeLimit
      }
    }
  }

  /**
   * 记录操作
   * @param key 监控标识
   * @param operation 操作名称
   */
  recordOperation(key: string, operation: string): void {
    const metrics = this.metrics.get(key)
    if (metrics) {
      metrics.operations?.push(`${operation} (${performance.now() - metrics.startTime}ms)`)
    }
  }

  /**
   * 记录渲染次数
   * @param key 监控标识
   */
  recordRender(key: string): void {
    const count = this.renderCounts.get(key) || 0
    this.renderCounts.set(key, count + 1)
  }

  /**
   * 停止性能监控
   * @param key 监控标识
   * @returns 性能指标
   */
  stop(key: string): PerformanceMetrics | null {
    const metrics = this.metrics.get(key)
    if (!metrics) return null

    metrics.endTime = performance.now()
    metrics.duration = metrics.endTime - metrics.startTime
    metrics.renderCount = this.renderCounts.get(key) || 0

    // 清理
    this.metrics.delete(key)
    this.renderCounts.delete(key)

    return metrics
  }

  /**
   * 获取当前性能指标
   * @param key 监控标识
   * @returns 当前性能指标
   */
  getCurrent(key: string): PerformanceMetrics | null {
    const metrics = this.metrics.get(key)
    if (!metrics) return null

    return {
      ...metrics,
      duration: performance.now() - metrics.startTime,
      renderCount: this.renderCounts.get(key) || 0
    }
  }

  /**
   * 测量函数执行时间
   * @param fn 要测量的函数
   * @param key 监控标识
   * @returns 函数执行结果和性能指标
   */
  async measure<T>(fn: () => Promise<T> | T, key: string): Promise<{ result: T; metrics: PerformanceMetrics }> {
    this.start(key)
    
    try {
      const result = await fn()
      const metrics = this.stop(key)!
      return { result, metrics }
    } catch (error) {
      this.stop(key)
      throw error
    }
  }

  /**
   * 创建性能装饰器
   * @param key 监控标识
   * @returns 装饰器函数
   */
  createDecorator(key: string) {
    return (target: any, propertyName: string, descriptor: PropertyDescriptor) => {
      const method = descriptor.value

      descriptor.value = async function (...args: any[]) {
        performanceMonitor.start(`${key}.${propertyName}`)
        
        try {
          const result = await method.apply(this, args)
          const metrics = performanceMonitor.stop(`${key}.${propertyName}`)
          
          if (process.env.NODE_ENV === 'development') {
            console.log(`Performance [${key}.${propertyName}]:`, metrics)
          }
          
          return result
        } catch (error) {
          performanceMonitor.stop(`${key}.${propertyName}`)
          throw error
        }
      }
    }
  }

  /**
   * 监控 Vue 组件性能
   * @param componentName 组件名称
   * @returns Vue 组件混入对象
   */
  createVueMixin(componentName: string) {
    return {
      beforeCreate() {
        performanceMonitor.start(`${componentName}.lifecycle`)
      },
      
      created() {
        performanceMonitor.recordOperation(`${componentName}.lifecycle`, 'created')
      },
      
      beforeMount() {
        performanceMonitor.recordOperation(`${componentName}.lifecycle`, 'beforeMount')
      },
      
      mounted() {
        performanceMonitor.recordOperation(`${componentName}.lifecycle`, 'mounted')
        const metrics = performanceMonitor.stop(`${componentName}.lifecycle`)
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`Component Performance [${componentName}]:`, metrics)
        }
      },
      
      beforeUpdate() {
        performanceMonitor.recordRender(componentName)
      },
      
      beforeUnmount() {
        // 清理可能残留的监控
        performanceMonitor.stop(`${componentName}.lifecycle`)
      }
    }
  }

  /**
   * 监控长列表渲染性能
   * @param listName 列表名称
   * @param itemCount 项目数量
   * @returns 性能监控对象
   */
  monitorListRendering(listName: string, itemCount: number) {
    const key = `${listName}.rendering`
    this.start(key)
    
    return {
      recordItemRender: (index: number) => {
        this.recordOperation(key, `item-${index}`)
      },
      
      finish: () => {
        const metrics = this.stop(key)
        if (metrics && process.env.NODE_ENV === 'development') {
          console.log(`List Rendering Performance [${listName}]:`, {
            ...metrics,
            itemCount,
            averageTimePerItem: metrics.duration! / itemCount
          })
        }
        return metrics
      }
    }
  }

  /**
   * 监控内存使用情况
   * @returns 内存使用信息
   */
  getMemoryUsage() {
    if ('memory' in performance) {
      const memory = (performance as any).memory
      return {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
        usedMB: Math.round(memory.usedJSHeapSize / 1048576 * 100) / 100,
        totalMB: Math.round(memory.totalJSHeapSize / 1048576 * 100) / 100
      }
    }
    return null
  }

  /**
   * 监控 FPS
   * @param duration 监控持续时间（毫秒）
   * @returns FPS 信息
   */
  monitorFPS(duration: number = 1000): Promise<{ fps: number; frames: number }> {
    return new Promise((resolve) => {
      let frames = 0
      const startTime = performance.now()
      
      const countFrame = () => {
        frames++
        const elapsed = performance.now() - startTime
        
        if (elapsed < duration) {
          requestAnimationFrame(countFrame)
        } else {
          const fps = Math.round((frames * 1000) / elapsed)
          resolve({ fps, frames })
        }
      }
      
      requestAnimationFrame(countFrame)
    })
  }

  /**
   * 创建性能报告
   * @returns 性能报告
   */
  generateReport() {
    const memory = this.getMemoryUsage()
    
    return {
      timestamp: new Date().toISOString(),
      memory,
      activeMonitors: Array.from(this.metrics.keys()),
      renderCounts: Object.fromEntries(this.renderCounts),
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    }
  }
}

// 导出单例实例
export const performanceMonitor = new PerformanceMonitor()

// 导出装饰器
export const measurePerformance = (key: string) => performanceMonitor.createDecorator(key)

// 导出 Vue 混入创建函数
export const createPerformanceMixin = (componentName: string) => 
  performanceMonitor.createVueMixin(componentName)

// 开发环境下的全局性能监控
if (process.env.NODE_ENV === 'development') {
  // 监控页面加载性能
  window.addEventListener('load', () => {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    if (nav) {
      const domContentLoaded = Math.max(0, nav.domContentLoadedEventEnd - nav.domContentLoadedEventStart)
      const loadComplete = Math.max(0, nav.loadEventEnd - nav.loadEventStart)
      const totalTime = Math.max(0, nav.loadEventEnd - nav.fetchStart)
      console.log('Page Load Performance:', { domContentLoaded, loadComplete, totalTime })
    }
  })
  
  // 定期报告内存使用情况
  setInterval(() => {
    const memory = performanceMonitor.getMemoryUsage()
    if (memory && memory.usedMB > 100) { // 超过 100MB 时警告
      console.warn('High Memory Usage:', memory)
    }
  }, 30000) // 每 30 秒检查一次
}

// 可见性优化开关，避免重复初始化
let visibilityOptimizationInitialized = false

/**
 * 设置页面可见性变更时的性能优化策略
 * - 页面隐藏时：记录事件，后续可在此处挂接暂停轮询/动画等逻辑
 * - 页面可见时：记录事件，后续可在此处恢复相关逻辑
 */
export function setupVisibilityOptimization(): void {
  if (visibilityOptimizationInitialized) return
  visibilityOptimizationInitialized = true

  const handleVisibilityChange = () => {
    const now = new Date().toISOString()
    if (document.hidden) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${now}] Page hidden - performance optimization active`)
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${now}] Page visible - performance optimization resumed`)
      }
    }
  }

  document.addEventListener('visibilitychange', handleVisibilityChange, { passive: true } as EventListenerOptions)
}

/**
 * 生成并返回一次性能报告（开发环境下会输出到控制台）
 */
export function generatePerformanceReport() {
  const report = performanceMonitor.generateReport()
  if (process.env.NODE_ENV === 'development') {
    console.log('Performance Report:', report)
  }
  return report
}